import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { config } from "../Constants";
import * as Sentry from "@sentry/react";
import { apiClient } from "../helper/api";
import { AppRoutes } from "../helper/appRoutes";

export interface apiResponse<Type> {
    // To match the response from the API
    status: boolean;
    message: string;
    data: Type;


}

export interface authData {
    userId: number
    userFirstName: string
    userLastName: string,
    userlastName: string,
    token: string
    userRoles: string
    profilePicture: string
    isSIPAccess: boolean
    partnerName: string
    partnersId: number
    isLicensingUser?: boolean
}

interface AuthContextType {

    user?: authData;
    loading: boolean;
    error?: any;
    /** Fetched during auth step; null until resolved. Not part of authData. */
    isDiligentSearchUser?: boolean | null;
    login: (authData: apiResponse<authData>) => void;
    //login: (response: { status: boolean; message: string; data: authData }) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType>(
    {} as AuthContextType
);

// Export the provider as we need to wrap the entire app with it
export function AuthProvider({ children }: { children: ReactNode; }): JSX.Element {
    const [user, setUser] = useState<authData>();
    const [error, setError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingInitial, setLoadingInitial] = useState<boolean>(true);
    const [isDiligentSearchUser, setIsDiligentSearchUser] = useState<boolean | null>(null);


    // We are using `react-router` for this example,
    // but feel free to omit this or use the
    // router of your choice.

    const location = useLocation();
    const navigate = useNavigate();
    // If we change page, reset the error state.
    useEffect(() => {
        if (error) setError(null);
    }, [location.pathname]);

    // Fetch isDiligentSearchUser during auth step (not stored in authData).
    useEffect(() => {
        if (!user?.token) {
            setIsDiligentSearchUser(null);
            return;
        }
        if (user?.userRoles.split(",").includes("Diligent Search User")) {
            setIsDiligentSearchUser(true);
            return;
        }
        else if (!user?.userRoles.split(",").includes("Client Admin")) {
            setIsDiligentSearchUser(false);
            return;
        }
        else {
            // Fetch from API when user is Client Admin but not Diligent Search User.
            apiClient("/DiligentSearch/isDiligentSearchUser", {
                method: "GET",
                headers: new Headers({
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user.token}`,
                }),
            })
                .then((responseJson) => {
                    setIsDiligentSearchUser(responseJson ? Boolean(responseJson.data.isDiligentSearchUser) : false);
                })
                .catch(() => {
                    setIsDiligentSearchUser(false);
                });
        }
    }, [user?.token]);

    // Check if there is a currently active session
    // when the provider is mounted for the first time.
    //
    // If there is an error, it means there is no session.
    //
    // Finally, just signal the component that the initial load
    // is over.
    useEffect(() => {

        setLoading(true);
        const currentUser = getCurrentUser();
        if (currentUser != null) {
            handleAlreadyLoggedIn(currentUser);
        }
        else {
            //send to login
            // logout();
            setLoading(false);
            setLoadingInitial(false);

        }


    }, []);

    function getCurrentUser() {
        const userStr = localStorage.getItem("user");
        if (userStr) return JSON.parse(userStr);

        return null;
    }


    async function handleAlreadyLoggedIn(authData: authData) {

        await IsAuthenticated(authData);

    }

    async function IsAuthenticated(authData: authData) {

        console.log("Calling /Auth/IsAuthenthenticated with token:", authData.token);
        await apiClient('/Auth/IsAuthenthenticated',
            {
                method: "GET",
                headers: new Headers({
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authData.token}`,
                    // 'Authorization': `Bearer ${authData.token}`,
                })
            })
            .then((responseJson) => {

                if (responseJson) {

                    setUser(authData);
                    Sentry.setUser({
                        id: authData.userId,
                        username: authData.userFirstName,
                    })
                }
                else {
                    logout();

                }
                setLoading(false);
                setLoadingInitial(false);

            }).catch((error) => {
                setLoading(false);
                setLoadingInitial(false);

            });


    }

    // Flags the component loading state and posts the login
    // data to the server.
    //
    // An error means that the email/password combination is
    // not valid.
    //
    // Finally, just signal the component that loading the
    // loading state is over.
    // OLd Function to login 
    function login(authData: any) {
        setLoading(true);
        if (authData.resp == 0 || authData.status == true) {
            setUser(authData.data);
            localStorage.setItem("user", JSON.stringify(authData.data));
            if (authData.data?.userRoles.split(",").includes("Customer User") ||
                authData.data?.userRoles.split(",").includes("State Reporter") ||
                authData.data?.userRoles.split(",").includes("Client Admin") ||
                authData.data?.userRoles.split(",").includes("Client Accounting") ||
                authData.data?.userRoles.split(",").includes("Client Operations") ||
                authData.data?.userRoles.split(",").includes("Client Processors") ||
                authData.data?.userRoles.split(",").includes("Diligent Search User")) {
                navigate(AppRoutes.client.home);
            } else {
                navigate(AppRoutes.home.base);
            }
        }
        else {
            setError(authData.message)
            navigate(AppRoutes.login);
        }
        setLoading(false);

    }


    //     // New Function to login 
    //     function login(response: { status: boolean; message: string; data: authData }) {
    //   setLoading(true);

    //   if (response.status) {
    //     // success
    //     setUser(response.data);
    //     localStorage.setItem("user", JSON.stringify(response.data));

    //     // decide where to navigate
    //     const roles = response.data.userRoles.split(",");
    //     const clientRoles = [
    //       "Customer User",
    //       "Client Admin",
    //       "Client Accounting",
    //       "Client Operations",
    //       "Client Processors"
    //     ];
    //     const dest = roles.some(r => clientRoles.includes(r)) ? "/client" : "/";
    //     navigate(dest);

    //   } else {
    //     // failure
    //     setError(response.message);
    //     navigate("/login");
    //   }

    //   setLoading(false);
    // }

    // Call the logout endpoint and then remove the user
    // from the state.
    function logout() {
        localStorage.removeItem("user");
        setUser(undefined);
        setIsDiligentSearchUser(null);
        navigate(AppRoutes.login);
        //sessionsApi.logout().then(() => setUser(undefined));
    }

    // Make the provider update only when it should.
    // We only want to force re-renders if the user,
    // loading or error states change.
    //
    // Whenever the `value` passed into a provider changes,
    // the whole tree under the provider re-renders, and
    // that can be very costly! Even in this case, where
    // you only get re-renders when logging in and out
    // we want to keep things very performant.
    const memoedValue = useMemo(
        () => ({
            user,
            loading,
            error,
            isDiligentSearchUser,
            login,
            logout,
        }),
        [user, loading, error, isDiligentSearchUser]
    );

    // We only want to render the underlying app after we
    // assert for the presence of a current user.
    return (
        <AuthContext.Provider value={memoedValue}>
            {!loadingInitial && children}
        </AuthContext.Provider>
    );
}

// Let's only export the `useAuth` hook instead of the context.
// We only want to use the hook directly and never the context component.
export default function useAuth() {
    return useContext(AuthContext);
}